import { useState, useEffect } from 'preact/hooks';
import { getBillingStatus, createPortal, type BillingStatus } from '../api/billing';
import { PortalBalanceTab } from './PortalBalanceTab';
import { PortalHistoryTab } from './PortalHistoryTab';
import { PortalReceiptsTab } from './PortalReceiptsTab';
import { UsageTab } from './UsageTab';

// ════════════════════════════════════════════════════════════════════════════
// PortalPage — customer-facing credits portal shell (valko-credits-platform
// Phase 3). Full-page route at /portal, profile-gated by `billingEnabled`
// (App.tsx). Mirrors AdminPage.tsx: `.app-container` + `.top-bar` + a tab button
// row + `{tab === x && <XTab/>}` delegation.
//
// COMPOSES existing Phase-2 pieces — it does NOT re-implement wallet state:
//   • Balance & top-up → PortalBalanceTab reads creditsStore selectors and drives
//     the already-mounted <TopUpModal/> (App.tsx) via openTopUpModal(null).
//   • Usage             → the existing <UsageTab isAdmin={false}/> verbatim.
//   • History / Receipts → read-only renderers of new backend read endpoints
//     (getCreditsHistory / getCreditsReceipts); graceful empty/soon state until
//     the backend lands.
//   • Stripe Customer Portal → shell-level deep-link button (createPortal),
//     shown only when a Stripe customer exists (prepaid-credits-only users have
//     no subscription/portal → a clear "prepaid credits" note instead).
//
// The wallet FSM lives entirely in creditsStore; the tabs are pure renderers of
// it (BalancePill precedent). History/Receipts fetch state is read-only local
// state (like UsageTab), NOT part of the credits FSM.
// ════════════════════════════════════════════════════════════════════════════

type Tab = 'balance' | 'usage' | 'history' | 'receipts';

const backLinkStyle: Record<string, string | number> = {
  color: 'var(--text-secondary, #8888aa)',
  fontSize: 12,
  textDecoration: 'none',
  border: '1px solid var(--border-medium, #4a4a6e)',
  padding: '6px 12px',
  borderRadius: 6,
};

export function PortalPage() {
  const [tab, setTab] = useState<Tab>('balance');
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  useEffect(() => {
    getBillingStatus()
      .then(setBilling)
      .catch(() => {
        /* status is best-effort — the portal still renders the credits surfaces */
      });
  }, []);

  // A Stripe Customer Portal exists only when the user is a Stripe customer
  // (subscription). Prepaid-credits-only users never subscribe → no portal;
  // gating avoids a hard 500 from createPortal() for those users.
  const hasStripePortal = !!(billing && (billing.hasSubscription || billing.stripeCustomerId));

  async function openStripePortal() {
    setPortalError(null);
    setPortalBusy(true);
    try {
      const { portalUrl } = await createPortal();
      window.location.href = portalUrl;
    } catch (e) {
      setPortalError(e instanceof Error ? e.message : 'Could not open the billing portal.');
      setPortalBusy(false);
    }
  }

  return (
    <div class="app-container" style={{ maxWidth: 900 }}>
      <div class="top-bar">
        <h1>Credits</h1>
        <a href="/" style={backLinkStyle}>Back</a>
      </div>

      {/* Shell-level Stripe Customer Portal deep-link (subscription customers only) */}
      <div style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {hasStripePortal ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              class="portal-stripe-btn"
              disabled={portalBusy}
              onClick={openStripePortal}
              style={{
                background: 'var(--bg-elevated, #1a1a2e)',
                color: 'var(--text-primary, #c8c8e8)',
                border: '1px solid var(--border-medium, #4a4a6e)',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                cursor: portalBusy ? 'default' : 'pointer',
              }}
            >
              {portalBusy ? 'Opening…' : 'Manage subscription & invoices ↗'}
            </button>
            <span style={{ fontSize: 11, color: 'var(--text-dim, #5a5a7e)' }}>
              Opens the secure Stripe billing portal.
            </span>
          </div>
        ) : (
          <p style={{ fontSize: 11, color: 'var(--text-dim, #5a5a7e)', margin: 0 }}>
            No billing portal — you're on prepaid credits (no subscription).
          </p>
        )}
        {portalError && (
          <div class="message error" style={{ margin: 0 }} role="alert">{portalError}</div>
        )}
      </div>

      {/* Tabs — same pattern as AdminPage's .admin-tabs */}
      <div class="admin-tabs">
        <button class={tab === 'balance' ? 'active' : ''} onClick={() => setTab('balance')}>
          Balance
        </button>
        <button class={tab === 'usage' ? 'active' : ''} onClick={() => setTab('usage')}>
          Usage
        </button>
        <button class={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>
          History
        </button>
        <button class={tab === 'receipts' ? 'active' : ''} onClick={() => setTab('receipts')}>
          Receipts
        </button>
      </div>

      {tab === 'balance' && <PortalBalanceTab />}
      {tab === 'usage' && <UsageTab isAdmin={false} />}
      {tab === 'history' && <PortalHistoryTab />}
      {tab === 'receipts' && <PortalReceiptsTab />}
    </div>
  );
}
